import React, { useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Loader2,
  Gift,
  Coins,
  Percent,
  Save,
  RefreshCw,
  TrendingUp,
  Activity,
  CheckCircle2,
  AlertTriangle,
  HelpCircle,
} from 'lucide-react';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useNotifications } from '@/hooks/useNotifications';
import {
  useReferralConfig,
  useUpdateBonusConfig,
  useUpdateShareConfig,
  useReferralConfigHistory,
} from '@/hooks/useReferralConfig';
import { UpdateBonusConfigPayload, UpdateShareConfigPayload } from '@/api/referralConfig';
import { formatDate } from '@/utils';

// Form Validation Schemas
const bonusSchema = z.object({
  bonusPool: z.coerce.number().min(0, 'Bonus pool must be 0 or greater'),
  qualifyingEvent: z.string().min(1, 'Qualifying event is required'),
  fallbackRule: z.string().min(1, 'Fallback rule is required'),
  layerSplits: z.record(z.coerce.number().min(0).max(100)),
  totalDistributionPercentage: z.coerce
    .number()
    .min(0)
    .max(100, 'Total distribution percentage must be between 0 and 100'),
});

const shareSchema = z.object({
  sharePercentage: z.coerce.number().min(0).max(100, 'Share percentage must be between 0 and 100'),
  shareModel: z.string().min(1, 'Share model is required'),
  fallbackRule: z.string().min(1, 'Fallback rule is required'),
  qualifyingActivities: z.array(z.string()),
  layerSplits: z.record(z.coerce.number().min(0).max(100)),
  totalDistributionPercentage: z.coerce
    .number()
    .min(0)
    .max(100, 'Total distribution percentage must be between 0 and 100'),
});

// Helper to normalize L1-L20 splits to exactly targetPercentage% and keep them in descending order (L1 is highest, then L2, L3...)
function normalizeSplits(
  splits: Record<string, number>,
  targetPercentage = 100
): Record<string, number> {
  const keys = Array.from({ length: 20 }, (_, i) => `L${i + 1}`);

  const cleanSplits: Record<string, number> = {};
  keys.forEach((key) => {
    const val = splits?.[key];
    const num = Number(val);
    cleanSplits[key] = !isNaN(num) && num > 0 ? num : 0;
  });

  const sum = keys.reduce((acc, key) => acc + (cleanSplits[key] ?? 0), 0);
  if (sum === 0) {
    const normalized: Record<string, number> = {};
    const equalShare = targetPercentage / 20;
    keys.forEach((key) => {
      normalized[key] = equalShare;
    });
    return normalized;
  }

  // Scale and round to 2 decimals
  const values: number[] = [];
  keys.forEach((key) => {
    const val = cleanSplits[key] ?? 0;
    const percent = (val / sum) * targetPercentage;
    const rounded = Math.round(percent * 100) / 100;
    values.push(rounded);
  });

  // Sort values descending so L1 is highest, down to L20
  values.sort((a, b) => b - a);

  // Calculate difference due to rounding and adjust the largest value (at index 0)
  let roundedSum = values.reduce((acc, val) => acc + val, 0);
  const diff = parseFloat((targetPercentage - roundedSum).toFixed(2));
  if (diff !== 0 && values.length > 0 && values[0] !== undefined) {
    values[0] = parseFloat((values[0] + diff).toFixed(2));
  }

  // Map values back to L1-L20 keys
  const sortedNormalized: Record<string, number> = {};
  keys.forEach((key, index) => {
    sortedNormalized[key] = values[index] ?? 0;
  });

  return sortedNormalized;
}

function formatDateTime(dateString: string | Date): string {
  const date = new Date(dateString);
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

export function ReferralConfigPage() {
  const { success, error: showToastError, warning: showToastWarning } = useNotifications();
  const { data: config, isLoading, isError, refetch } = useReferralConfig();
  const [historyTake, setHistoryTake] = useState(10);
  const {
    data: history,
    isLoading: isHistoryLoading,
    isFetching: isHistoryFetching,
  } = useReferralConfigHistory({ skip: 0, take: historyTake });

  const hasMoreHistory = history ? history.length === historyTake : false;

  const { mutate: updateBonus, isPending: isUpdatingBonus } = useUpdateBonusConfig();
  const { mutate: updateShare, isPending: isUpdatingShare } = useUpdateShareConfig();

  const levels = Array.from({ length: 20 }, (_, i) => `L${i + 1}`);

  // 1. Referral Bonus Schedule Form
  const {
    register: registerBonus,
    handleSubmit: handleSubmitBonus,
    reset: resetBonus,
    watch: watchBonus,
    setValue: setValueBonus,
    control: controlBonus,
    formState: { errors: errorsBonus, isDirty: isDirtyBonus },
  } = useForm<UpdateBonusConfigPayload>({
    resolver: zodResolver(bonusSchema),
    defaultValues: {
      bonusPool: 0,
      qualifyingEvent: 'account_activation',
      fallbackRule: 'CompanyReserve',
      layerSplits: {},
      totalDistributionPercentage: 100,
    },
  });

  // 2. Dynamic Upline Share Form
  const {
    register: registerShare,
    handleSubmit: handleSubmitShare,
    reset: resetShare,
    watch: watchShare,
    setValue: setValueShare,
    control: controlShare,
    formState: { errors: errorsShare, isDirty: isDirtyShare },
  } = useForm<UpdateShareConfigPayload>({
    resolver: zodResolver(shareSchema),
    defaultValues: {
      sharePercentage: 0,
      shareModel: 'Additive',
      fallbackRule: 'CompanyReserve',
      qualifyingActivities: [],
      layerSplits: {},
      totalDistributionPercentage: 100,
    },
  });

  useEffect(() => {
    if (config) {
      resetBonus({
        bonusPool: config.bonusConfig?.bonusPool || 0,
        qualifyingEvent: config.bonusConfig?.qualifyingEvent || 'account_activation',
        fallbackRule: config.bonusConfig?.fallbackRule || 'CompanyReserve',
        layerSplits: config.bonusConfig?.layerSplits || {},
        totalDistributionPercentage: config.bonusConfig?.totalDistributionPercentage || 100,
      });

      resetShare({
        sharePercentage: config.shareConfig?.sharePercentage || 0,
        shareModel: config.shareConfig?.shareModel || 'Additive',
        fallbackRule: config.shareConfig?.fallbackRule || 'CompanyReserve',
        qualifyingActivities: config.shareConfig?.qualifyingActivities || [],
        layerSplits: config.shareConfig?.layerSplits || {},
        totalDistributionPercentage: config.shareConfig?.totalDistributionPercentage || 100,
      });
    }
  }, [config, resetBonus, resetShare]);

  // Watch layer percentages to compute real-time sums
  const watchedBonusSplits = watchBonus('layerSplits') || {};
  const sumBonus = Object.values(watchedBonusSplits).reduce(
    (sum, val) => sum + (Number(val) || 0),
    0
  );
  const formattedSumBonus = parseFloat(sumBonus.toFixed(2));
  const watchedTotalBonusPercent = watchBonus('totalDistributionPercentage') ?? 100;
  const isBonusSumValid = Math.abs(formattedSumBonus - watchedTotalBonusPercent) < 0.001;

  const watchedShareSplits = watchShare('layerSplits') || {};
  const sumShare = Object.values(watchedShareSplits).reduce(
    (sum, val) => sum + (Number(val) || 0),
    0
  );
  const formattedSumShare = parseFloat(sumShare.toFixed(2));
  const watchedTotalSharePercent = watchShare('totalDistributionPercentage') ?? 100;
  const isShareSumValid = Math.abs(formattedSumShare - watchedTotalSharePercent) < 0.001;

  // Earnings activities options
  const activityOptions = [
    { value: 'successful_join', label: 'Successful join' },
    { value: 'daily_streak', label: 'Daily Streak' },
    { value: 'voucher_redeem', label: 'Voucher Redeem' },
  ];

  const currentActivities = watchShare('qualifyingActivities') || [];

  const handleToggleActivity = (activity: string) => {
    const nextActivities = currentActivities.includes(activity)
      ? currentActivities.filter((a) => a !== activity)
      : [...currentActivities, activity];
    setValueShare('qualifyingActivities', nextActivities, { shouldDirty: true });
  };

  const handleNormalizeBonus = () => {
    const normalized = normalizeSplits(watchedBonusSplits, watchedTotalBonusPercent);
    setValueBonus('layerSplits', normalized, { shouldDirty: true });
    success(`Bonus layer percentages normalized to ${watchedTotalBonusPercent}%`);
  };

  const handleNormalizeShare = () => {
    const normalized = normalizeSplits(watchedShareSplits, watchedTotalSharePercent);
    setValueShare('layerSplits', normalized, { shouldDirty: true });
    success(`Share layer splits normalized to ${watchedTotalSharePercent}%`);
  };

  const onSubmitBonus = (data: UpdateBonusConfigPayload) => {
    // if (!isBonusSumValid) {
    //   showToastWarning(
    //     `Bonus splits sum is ${formattedSumBonus}%. It should equal ${watchedTotalBonusPercent}% for proper network distribution.`
    //   );
    // }
    updateBonus(data, {
      onSuccess: () => {
        success('Referral bonus schedule updated successfully!');
      },
      onError: (err) => {
        const errMsg = err.response?.data?.message || 'Failed to update bonus configuration';
        showToastError(errMsg);
      },
    });
  };

  const onSubmitShare = (data: UpdateShareConfigPayload) => {
    // if (!isShareSumValid) {
    //   showToastWarning(
    //     `Share splits sum is ${formattedSumShare}%. It should equal ${watchedTotalSharePercent}% for proper network distribution.`
    //   );
    // }
    updateShare(data, {
      onSuccess: () => {
        success('Dynamic upline share configuration updated successfully!');
      },
      onError: (err) => {
        const errMsg = err.response?.data?.message || 'Failed to update share configuration';
        showToastError(errMsg);
      },
    });
  };

  if (isLoading) {
    return (
      <div className="flex h-[400px] flex-col items-center justify-center space-y-4">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <p className="text-sm text-muted-foreground animate-pulse">Loading referral settings...</p>
      </div>
    );
  }

  if (isError || !config) {
    return (
      <div className="flex h-[400px] flex-col items-center justify-center space-y-4 rounded-lg border border-dashed border-destructive/50 bg-destructive/5 p-8 text-center">
        <h3 className="text-lg font-semibold text-destructive">
          Failed to Load Referral Configuration
        </h3>
        <p className="max-w-xs text-sm text-muted-foreground">
          There was an error loading the referral bonus and upline configs.
        </p>
        <Button onClick={() => refetch()} variant="outline" className="flex items-center gap-2">
          <RefreshCw className="h-4 w-4" />
          Retry
        </Button>
      </div>
    );
  }

  const formatMsr = (val: number) => {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(val);
  };

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Page Header */}
      <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-3xl font-extrabold tracking-tight">Admin Configuration</h1>
          </div>
          <p className="text-muted-foreground mt-1 max-w-3xl text-sm leading-relaxed">
            Distribution schedules and the dynamic share are admin-configurable with versioning and
            effective dates — no code deployment required. Changes apply to future events only.
          </p>
        </div>
      </div>

      {/* Network Treasury Stats */}
      <div className="space-y-3 w-full">
        <h2 className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
          Treasury (Network-Wide)
        </h2>
        <div className="grid gap-6 md:grid-cols-2 max-w-4xl w-full">
          <Card className="border-border bg-card shadow-sm transition-all hover:shadow-md w-full">
            <CardContent className="p-6">
              <span className="text-[11px] font-bold text-muted-foreground uppercase tracking-wider">
                Total MSR Minted
              </span>
              <div className="mt-2 flex items-baseline gap-2">
                <span className="text-3xl font-extrabold tracking-tight">
                  {formatMsr(config.treasury?.totalMinted || 0)}
                </span>
                <span className="text-sm font-semibold text-primary">MSR</span>
              </div>
              <p className="text-xs text-muted-foreground mt-1">Across the whole network</p>
            </CardContent>
          </Card>

          <Card className="border-border bg-card shadow-sm transition-all hover:shadow-md w-full">
            <CardContent className="p-6">
              <span className="text-[11px] font-bold text-muted-foreground uppercase tracking-wider">
                Company Reserve Pool
              </span>
              <div className="mt-2 flex items-baseline gap-2">
                <span className="text-3xl font-extrabold tracking-tight">
                  {formatMsr(config.treasury?.reservePool || 0)}
                </span>
                <span className="text-sm font-semibold text-primary">MSR</span>
              </div>
              <p className="text-xs text-muted-foreground mt-1">Unallocated shallow-tree shares</p>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Form 1: Referral Bonus Schedule (FR-03) */}
      <form onSubmit={handleSubmitBonus(onSubmitBonus)}>
        <Card className="border-border bg-card shadow-md">
          <CardHeader className="border-b border-border/40 pb-4">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center space-x-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-pink-100 dark:bg-pink-900/30 text-pink-600 dark:text-pink-400">
                  <Gift className="h-5 w-5" />
                </div>
                <div>
                  <CardTitle className="text-lg font-bold">Referral bonus schedule</CardTitle>
                  <CardDescription className="text-xs mt-0.5">
                    Define the flat pool payout trigger and structure per layer.
                  </CardDescription>
                </div>
              </div>

              {/* Version & Validation Badges */}
              <div className="flex items-center gap-2">
                <Badge
                  variant="outline"
                  className="text-xs font-semibold px-2.5 py-1 text-slate-600 dark:text-slate-300"
                >
                  v{config.bonusConfig?.id || 0} ·{' '}
                  {config.bonusConfig ? formatDate(config.bonusConfig.effectiveFrom) : ''}
                </Badge>
                <Badge
                  variant={isBonusSumValid ? 'default' : 'destructive'}
                  className={`text-xs font-semibold px-2.5 py-1 flex items-center gap-1 border-none ${
                    isBonusSumValid
                      ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300'
                      : 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300'
                  }`}
                >
                  {isBonusSumValid ? (
                    <>
                      <CheckCircle2 className="h-3 w-3" />Σ {watchedTotalBonusPercent}%
                    </>
                  ) : (
                    <>
                      <AlertTriangle className="h-3 w-3" />Σ {formattedSumBonus}% /{' '}
                      {watchedTotalBonusPercent}%
                    </>
                  )}
                </Badge>
              </div>
            </div>
          </CardHeader>

          <CardContent className="p-6 space-y-6">
            {/* Top configuration parameters */}
            <div className="grid gap-6 sm:grid-cols-4">
              <div className="space-y-2">
                <Label
                  htmlFor="bonusPool"
                  className="text-xs font-bold uppercase tracking-wider text-muted-foreground"
                >
                  Bonus Pool (MSR / Event)
                </Label>
                <Input
                  id="bonusPool"
                  type="number"
                  disabled={isUpdatingBonus}
                  {...registerBonus('bonusPool')}
                  className={errorsBonus.bonusPool ? 'border-destructive' : ''}
                />
                {errorsBonus.bonusPool && (
                  <p className="text-xs text-destructive mt-1 font-medium">
                    {errorsBonus.bonusPool.message}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label
                  htmlFor="qualifyingEvent"
                  className="text-xs font-bold uppercase tracking-wider text-muted-foreground"
                >
                  Qualifying Event
                </Label>
                <Controller
                  name="qualifyingEvent"
                  control={controlBonus}
                  render={({ field }) => (
                    <Select
                      disabled={isUpdatingBonus}
                      value={field.value}
                      onValueChange={field.onChange}
                    >
                      <SelectTrigger id="qualifyingEvent">
                        <SelectValue placeholder="Select qualifying event" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="account_activation">Account activation</SelectItem>
                        <SelectItem value="first_deposit">First deposit</SelectItem>
                        {/* <SelectItem value="daily_streak">Daily streak</SelectItem> */}
                        <SelectItem value="first_voucher_redeem">First Voucher redeem</SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                />
              </div>

              <div className="space-y-2">
                <Label
                  htmlFor="fallbackRuleBonus"
                  className="text-xs font-bold uppercase tracking-wider text-muted-foreground"
                >
                  Shallow-Tree Fallback
                </Label>
                <Controller
                  name="fallbackRule"
                  control={controlBonus}
                  render={({ field }) => (
                    <Select
                      disabled={isUpdatingBonus}
                      value={field.value}
                      onValueChange={field.onChange}
                    >
                      <SelectTrigger id="fallbackRuleBonus">
                        <SelectValue placeholder="Select fallback rule" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="CompanyReserve">Company reserve pool</SelectItem>
                        <SelectItem value="Redistribute">Redistribute to filled layers</SelectItem>
                        {/* <SelectItem value="Forfeit">Forfeit (not minted)</SelectItem> */}
                      </SelectContent>
                    </Select>
                  )}
                />
              </div>

              <div className="space-y-2">
                <Label
                  htmlFor="totalDistributionPercentageBonus"
                  className="text-xs font-bold uppercase tracking-wider text-muted-foreground"
                >
                  Total Distribution (%)
                </Label>
                <Input
                  id="totalDistributionPercentageBonus"
                  type="number"
                  disabled={isUpdatingBonus}
                  {...registerBonus('totalDistributionPercentage')}
                  className={errorsBonus.totalDistributionPercentage ? 'border-destructive' : ''}
                />
                {errorsBonus.totalDistributionPercentage && (
                  <p className="text-xs text-destructive mt-1 font-medium">
                    {errorsBonus.totalDistributionPercentage.message}
                  </p>
                )}
              </div>
            </div>

            {/* Per-Layer Splits */}
            <div className="space-y-4 pt-4 border-t border-border/40">
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                  Per-layer percentages
                </span>
                <button
                  type="button"
                  onClick={handleNormalizeBonus}
                  className="text-xs font-semibold text-primary hover:underline"
                >
                  Normalize to {watchedTotalBonusPercent}%
                </button>
              </div>

              <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
                {levels.map((level) => (
                  <div
                    key={level}
                    className="flex items-center rounded-lg border bg-slate-50 dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-sm overflow-hidden h-10 transition-all hover:border-slate-300 dark:hover:border-slate-700"
                  >
                    <span className="bg-slate-200/50 dark:bg-slate-850 px-3 py-2 text-slate-500 font-semibold min-w-[3rem] text-center border-r select-none h-full flex items-center justify-center">
                      {level}
                    </span>
                    <input
                      type="number"
                      step="any"
                      disabled={isUpdatingBonus}
                      {...registerBonus(`layerSplits.${level}` as any)}
                      className="w-full bg-transparent px-2 text-right font-medium outline-none focus:ring-0 focus:border-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none h-full"
                    />
                    <span className="text-slate-400 dark:text-slate-500 pr-3 font-semibold select-none">
                      %
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end pt-4 border-t border-border/40">
              <Button
                type="submit"
                disabled={isUpdatingBonus || !isDirtyBonus}
                className="flex items-center gap-2 font-semibold shadow-sm px-6"
              >
                {isUpdatingBonus ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Save className="h-4 w-4" />
                )}
                Save configuration
              </Button>
            </div>
          </CardContent>
        </Card>
      </form>

      {/* Form 2: Dynamic Upline Share (FR-04) */}
      <form onSubmit={handleSubmitShare(onSubmitShare)}>
        <Card className="border-border bg-card shadow-md">
          <CardHeader className="border-b border-border/40 pb-4">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center space-x-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400">
                  <TrendingUp className="h-5 w-5" />
                </div>
                <div>
                  <CardTitle className="text-lg font-bold">Dynamic upline share</CardTitle>
                  <CardDescription className="text-xs mt-0.5">
                    Configure direct earnings splits forwarded upward to referral uplines.
                  </CardDescription>
                </div>
              </div>

              {/* Version & Validation Badges */}
              <div className="flex items-center gap-2">
                <Badge
                  variant="outline"
                  className="text-xs font-semibold px-2.5 py-1 text-slate-600 dark:text-slate-300"
                >
                  v{config.shareConfig?.id || 0} ·{' '}
                  {config.shareConfig ? formatDate(config.shareConfig.effectiveFrom) : ''}
                </Badge>
                <Badge
                  variant={isShareSumValid ? 'default' : 'destructive'}
                  className={`text-xs font-semibold px-2.5 py-1 flex items-center gap-1 border-none ${
                    isShareSumValid
                      ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300'
                      : 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300'
                  }`}
                >
                  {isShareSumValid ? (
                    <>
                      <CheckCircle2 className="h-3 w-3" />Σ {watchedTotalSharePercent}%
                    </>
                  ) : (
                    <>
                      <AlertTriangle className="h-3 w-3" />Σ {formattedSumShare}% /{' '}
                      {watchedTotalSharePercent}%
                    </>
                  )}
                </Badge>
              </div>
            </div>
          </CardHeader>

          <CardContent className="p-6 space-y-6">
            {/* Top configuration parameters */}
            <div className="grid gap-6 sm:grid-cols-4">
              <div className="space-y-2">
                <Label
                  htmlFor="sharePercentage"
                  className="text-xs font-bold uppercase tracking-wider text-muted-foreground"
                >
                  Share of Earnings (%)
                </Label>
                <Input
                  id="sharePercentage"
                  type="number"
                  disabled={isUpdatingShare}
                  {...registerShare('sharePercentage')}
                  className={errorsShare.sharePercentage ? 'border-destructive' : ''}
                />
                {errorsShare.sharePercentage && (
                  <p className="text-xs text-destructive mt-1 font-medium">
                    {errorsShare.sharePercentage.message}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label
                  htmlFor="shareModel"
                  className="text-xs font-bold uppercase tracking-wider text-muted-foreground"
                >
                  Model
                </Label>
                <Controller
                  name="shareModel"
                  control={controlShare}
                  render={({ field }) => (
                    <Select
                      disabled={isUpdatingShare}
                      value={field.value}
                      onValueChange={field.onChange}
                    >
                      <SelectTrigger id="shareModel">
                        <SelectValue placeholder="Select share model" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Additive">Additive (company-funded)</SelectItem>
                        <SelectItem value="Deductive">Deductive ( From earner)</SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                />
              </div>

              <div className="space-y-2">
                <Label
                  htmlFor="fallbackRuleShare"
                  className="text-xs font-bold uppercase tracking-wider text-muted-foreground"
                >
                  Shallow-Tree Fallback
                </Label>
                <Controller
                  name="fallbackRule"
                  control={controlShare}
                  render={({ field }) => (
                    <Select
                      disabled={isUpdatingShare}
                      value={field.value}
                      onValueChange={field.onChange}
                    >
                      <SelectTrigger id="fallbackRuleShare">
                        <SelectValue placeholder="Select fallback rule" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="CompanyReserve">Company reserve pool</SelectItem>
                        <SelectItem value="Redistribute">Redistribute to filled layers</SelectItem>
                        {/* <SelectItem value="Forfeit">Forfeit (not minted)</SelectItem> */}
                      </SelectContent>
                    </Select>
                  )}
                />
              </div>

              <div className="space-y-2">
                <Label
                  htmlFor="totalDistributionPercentageShare"
                  className="text-xs font-bold uppercase tracking-wider text-muted-foreground"
                >
                  Total Distribution (%)
                </Label>
                <Input
                  id="totalDistributionPercentageShare"
                  type="number"
                  disabled={isUpdatingShare}
                  {...registerShare('totalDistributionPercentage')}
                  className={errorsShare.totalDistributionPercentage ? 'border-destructive' : ''}
                />
                {errorsShare.totalDistributionPercentage && (
                  <p className="text-xs text-destructive mt-1 font-medium">
                    {errorsShare.totalDistributionPercentage.message}
                  </p>
                )}
              </div>
            </div>

            {/* Qualifying Activities badge toggles */}
            <div className="space-y-3 pt-4 border-t border-border/40">
              <div className="flex items-center space-x-1 text-xs font-bold uppercase tracking-wider text-muted-foreground">
                <Activity className="h-3.5 w-3.5" />
                <span>Earning activities that share to upline</span>
              </div>
              <div className="flex flex-wrap gap-2.5">
                {activityOptions.map((act) => {
                  const isSelected = currentActivities.includes(act.value);
                  return (
                    <button
                      key={act.value}
                      type="button"
                      disabled={isUpdatingShare}
                      onClick={() => handleToggleActivity(act.value)}
                      className={`inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-xs font-semibold border transition-all ${
                        isSelected
                          ? 'bg-primary text-primary-foreground border-primary hover:bg-primary/95 shadow-sm'
                          : 'bg-background text-muted-foreground border-border hover:border-slate-400 hover:text-foreground'
                      }`}
                    >
                      {isSelected && <CheckCircle2 className="h-3 w-3" />}
                      {act.label}
                    </button>
                  );
                })}
              </div>
              <p className="text-[11px] text-muted-foreground leading-normal max-w-2xl pt-1">
                Only selected activities distribute a share upward. Unselected ones still earn MSR
                for the user but nothing goes to their upline.
              </p>
            </div>

            {/* Per-Layer Splits of Shared Amount */}
            <div className="space-y-4 pt-4 border-t border-border/40">
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                  Per-layer split of the shared amount
                </span>
                <button
                  type="button"
                  onClick={handleNormalizeShare}
                  className="text-xs font-semibold text-primary hover:underline"
                >
                  Normalize to {watchedTotalSharePercent}%
                </button>
              </div>

              <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
                {levels.map((level) => (
                  <div
                    key={level}
                    className="flex items-center rounded-lg border bg-slate-50 dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-sm overflow-hidden h-10 transition-all hover:border-slate-300 dark:hover:border-slate-700"
                  >
                    <span className="bg-slate-200/50 dark:bg-slate-850 px-3 py-2 text-slate-500 font-semibold min-w-[3rem] text-center border-r select-none h-full flex items-center justify-center">
                      {level}
                    </span>
                    <input
                      type="number"
                      step="any"
                      disabled={isUpdatingShare}
                      {...registerShare(`layerSplits.${level}` as any)}
                      className="w-full bg-transparent px-2 text-right font-medium outline-none focus:ring-0 focus:border-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none h-full"
                    />
                    <span className="text-slate-400 dark:text-slate-500 pr-3 font-semibold select-none">
                      %
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end pt-4 border-t border-border/40">
              <Button
                type="submit"
                disabled={isUpdatingShare || !isDirtyShare}
                className="flex items-center gap-2 font-semibold shadow-sm px-6"
              >
                {isUpdatingShare ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Save className="h-4 w-4" />
                )}
                Save configuration
              </Button>
            </div>
          </CardContent>
        </Card>
      </form>

      {/* Configuration change history */}
      <Card className="border-border bg-card shadow-md">
        <CardContent className="p-6">
          <h3 className="text-base font-bold text-card-foreground mb-6">
            Configuration change history
          </h3>
          {isHistoryLoading ? (
            <div className="flex justify-center py-6">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : !history || history.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4">No configuration history found.</p>
          ) : (
            <div className="space-y-4">
              <div className="space-y-4">
                {history.map((item, index) => (
                  <div key={item.id}>
                    {index > 0 && <hr className="border-border/40 my-4" />}
                    <div className="flex items-center gap-4">
                      {item.type === 'share' ? (
                        <span className="text-[10px] uppercase font-bold tracking-wider rounded-md px-2.5 py-1.5 bg-purple-100 text-purple-700 dark:bg-purple-950/40 dark:text-purple-300 border border-purple-200/50 dark:border-purple-900/30 whitespace-nowrap">
                          SHARE V{item.version}
                        </span>
                      ) : (
                        <span className="text-[10px] uppercase font-bold tracking-wider rounded-md px-2.5 py-1.5 bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300 border border-emerald-200/50 dark:border-emerald-900/30 whitespace-nowrap">
                          BONUS V{item.version}
                        </span>
                      )}
                      <div className="flex flex-col">
                        <span className="text-sm font-medium text-foreground leading-normal">
                          {item.title}
                        </span>
                        <span className="text-xs text-muted-foreground mt-1">
                          {formatDateTime(item.createdAt)}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {hasMoreHistory && (
                <div className="pt-4 flex justify-center border-t mt-4">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={isHistoryFetching}
                    onClick={() => setHistoryTake((prev) => prev + 10)}
                    className="w-full sm:w-auto"
                  >
                    {isHistoryFetching ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Loading...
                      </>
                    ) : (
                      'View More'
                    )}
                  </Button>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
